import { topGrossingServerApi } from '../../api/top-grossing'
import { Game } from '../../features/game/types/Game'
import { dashboardGameFields, outdatedGameFields, pinnedGameFields } from '../features/dashboard/helpers/dashboardHelpers'
import { DashboardOutdatedGames, DashboardSummary, DashboardVersion, GameNotesMap, TopGrossingRankedGame } from '../features/dashboard/types/Dashboard'
import { optionalJson } from './helpers'
import { internalApiserverApi } from './internal-apiserver'

const marketSettingUrl = 'users/settings/internal_selected_market_iso'
const dashboardParams = (marketIso: string, fields = dashboardGameFields) => ({ marketIso, include: fields.join(',') })
const toGames = (games: Game[] | null) => (games ?? []).map((game) => new Game(game))
const toStoredIso = (setting: { iso?: string } | null) => setting?.iso || null

const dashboardApi = internalApiserverApi.enhanceEndpoints({ addTagTypes: ['dashboardMarket'] }).injectEndpoints({
  endpoints: (builder) => ({
    // Only these two counts: the answer has no count of the games not updated in 6 months.
    getDashboardSummary: builder.query<DashboardSummary, string>({
      query: (marketIso) => ({ url: 'i/games/dashboard', params: dashboardParams(marketIso) }),
      transformResponse: (summary: Partial<DashboardSummary> | null) => ({ withoutAnalysis: summary?.withoutAnalysis ?? 0, outdated: summary?.outdated ?? 0 }),
    }),
    getDashboardGamesWithoutAnalysis: builder.query<Game[], string>({
      query: (marketIso) => ({ url: 'i/games/dashboard/withoutanalysis', params: dashboardParams(marketIso) }),
      transformResponse: toGames,
    }),
    // `versions` is keyed by app id.
    getDashboardOutdatedGames: builder.query<DashboardOutdatedGames, string>({
      query: (marketIso) => ({ url: 'i/games/dashboard/outdated', params: dashboardParams(marketIso, outdatedGameFields) }),
      transformResponse: (answer: { outdated?: Game[]; versions?: Record<string, DashboardVersion> } | null) => ({
        games: toGames(answer?.outdated ?? []),
        versions: answer?.versions ?? {},
      }),
    }),
    getDashboardGamesNotUpdated: builder.query<Game[], string>({
      query: (marketIso) => ({ url: 'i/games/dashboard/notupdatedinsixmonths', params: dashboardParams(marketIso) }),
      transformResponse: toGames,
    }),
    // Game id → number of FTUE videos. Answers 201.
    getFtueVideoCounts: builder.query<Record<string, number>, void>({
      query: () => 'ftue-videos/count/map',
      transformResponse: (counts: Record<string, number> | null) => counts ?? {},
    }),
    // A read, although it's a POST.
    getGameNotes: builder.query<GameNotesMap, string[]>({
      query: (gameIds) => ({ url: 'analyses/notes/list', method: 'POST', body: { gameIds } }),
      transformResponse: (answer: { notesMap?: GameNotesMap } | null) => answer?.notesMap ?? {},
    }),
    // One id answers `{ game }` and fails without `marketIso`. Ids with no game are left out.
    getDashboardPinnedGames: builder.query<Game[], { ids: string[]; marketIso: string }>({
      query: ({ ids, marketIso }) => ({ url: `games/${ids.join(',')}`, params: { include: pinnedGameFields.join(','), gameOnly: true, marketIso } }),
      transformResponse: (answer: Game[] | { game?: Game } | null, _meta, { ids }) => {
        const games = Array.isArray(answer) ? answer : answer?.game ? [answer.game] : []
        return games.filter((game) => ids.includes(game.id)).map((game) => new Game(game))
      },
    }),
    // `null` when the account has none.
    getDashboardMarket: builder.query<string | null, void>({
      query: () => ({ url: marketSettingUrl, responseHandler: optionalJson }),
      transformResponse: toStoredIso,
      providesTags: ['dashboardMarket'],
    }),
    // Returns the iso as stored afterwards.
    saveDashboardMarket: builder.mutation<string | null, string>({
      async queryFn(iso, _api, _extraOptions, baseQuery) {
        const saved = await baseQuery({ url: marketSettingUrl, method: 'PUT', body: { iso }, responseHandler: 'text' })
        if (saved.error) return { error: saved.error }
        const stored = await baseQuery({ url: marketSettingUrl, responseHandler: optionalJson })
        if (stored.error) return { error: stored.error }
        return { data: toStoredIso(stored.data as { iso?: string } | null) }
      },
      async onQueryStarted(_iso, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          dispatch(dashboardApi.util.upsertQueryData('getDashboardMarket', undefined, data))
        } catch {}
      },
    }),
  }),
})

const dashboardTopGrossingApi = topGrossingServerApi.injectEndpoints({
  endpoints: (builder) => ({
    // Keyed by app id; apps top-grossing doesn't know are left out.
    getTopGamesByAppIds: builder.query<Record<string, TopGrossingRankedGame>, { marketIso: string; appIds: number[] }>({
      query: ({ marketIso, appIds }) => `/games/${encodeURIComponent(marketIso)}/games/${appIds.join(',')}`,
      transformResponse: (answer: Record<string, TopGrossingRankedGame> | TopGrossingRankedGame[] | null) => {
        if (!answer) return {}
        const games = Array.isArray(answer) ? answer : Object.values(answer)
        return games.reduce<Record<string, TopGrossingRankedGame>>((result, game) => {
          if (game?.appId) result[String(game.appId)] = game
          return result
        }, {})
      },
    }),
  }),
})

export const {
  useGetDashboardSummaryQuery,
  useGetDashboardGamesWithoutAnalysisQuery,
  useGetDashboardOutdatedGamesQuery,
  useGetDashboardGamesNotUpdatedQuery,
  useGetFtueVideoCountsQuery,
  useGetGameNotesQuery,
  useGetDashboardPinnedGamesQuery,
  useGetDashboardMarketQuery,
  useSaveDashboardMarketMutation,
} = dashboardApi

export const { useGetTopGamesByAppIdsQuery } = dashboardTopGrossingApi
